/** @format */

import { Brand, CreateImportInput, ImportJob } from "@question-bank/shared";
import { getApiBaseUrl, buildApiError } from "./questions";
import { authFetch } from "./session";

const JSON_HEADERS: HeadersInit = {
	"Content-Type": "application/json",
};

interface ImportErrorDetail {
	field?: string;
	message: string;
}

interface ImportErrorBody {
	error?: { message?: string; details?: ImportErrorDetail[] };
}

const describeDetail = (detail: ImportErrorDetail): string =>
	detail.field ? `${detail.field}: ${detail.message}` : detail.message;

/**
 * Build a readable error for a rejected import. Unlike `buildApiError`, each
 * detail keeps its `field`: the row index in it is the only thing that tells
 * the admin which line of the CSV to fix, and the import toast has no
 * per-field slot to show it in.
 *
 * @param response - The failed response from the imports endpoint.
 * @returns An Error naming the offending rows, or carrying the status text when the body is not structured.
 */
const buildImportError = async (response: Response): Promise<Error> => {
	const body = (await response.json().catch(() => null)) as ImportErrorBody | null;
	const apiError = body?.error;
	if (apiError?.message) {
		const detailMessages = apiError.details?.map(describeDetail) ?? [];
		const suffix = detailMessages.length > 0 ? `: ${detailMessages.join("; ")}` : "";
		return new Error(`${apiError.message}${suffix}`);
	}

	return new Error(`Failed to import questions: ${response.statusText}`);
};

/**
 * Record a CSV import. The server validates every row, publishes a create event
 * for each question and returns the job that audits them (requires auth). The
 * questions are written after the response returns.
 */

export const createImport = async (brand: Brand, input: CreateImportInput): Promise<ImportJob> => {
	const baseUrl = getApiBaseUrl(brand);
	const response = await authFetch(`${baseUrl}/imports`, {
		method: "POST",
		headers: JSON_HEADERS,
		body: JSON.stringify(input),
	});

	if (!response.ok) {
		throw await buildImportError(response);
	}

	const body = (await response.json()) as { data: ImportJob };
	return body.data;
};

/**
 * List every import job for a brand, newest first.
 */
export const listImports = async (brand: Brand): Promise<ImportJob[]> => {
	const baseUrl = getApiBaseUrl(brand);
	const response = await authFetch(`${baseUrl}/imports`, { method: "GET", headers: JSON_HEADERS });

	if (!response.ok) {
		throw await buildApiError(response, "Failed to list imports");
	}

	const body = (await response.json()) as { data: ImportJob[] };
	return body.data;
};

/**
 * Get a single import job by id.
 */

export const getImport = async (brand: Brand, jobId: string): Promise<ImportJob> => {
	const baseUrl = getApiBaseUrl(brand);
	const response = await authFetch(`${baseUrl}/imports/${encodeURIComponent(jobId)}`, {
		method: "GET",
		headers: JSON_HEADERS,
	});

	if (!response.ok) {
		throw await buildApiError(response, "Failed to get import");
	}

	const body = (await response.json()) as { data: ImportJob };
	return body.data;
};

/**
 * Roll an import back: the server publishes a delete event for each question it
 * created (skipping any saved since, missing or already deleted) and returns the
 * job marked `rolled_back`. The soft deletes are written after the response returns.
 */

export const rollbackImport = async (brand: Brand, jobId: string): Promise<ImportJob> => {
	const baseUrl = getApiBaseUrl(brand);
	const response = await authFetch(`${baseUrl}/imports/${encodeURIComponent(jobId)}/rollback`, {
		method: "POST",
		headers: JSON_HEADERS,
	});

	if (!response.ok) {
		throw await buildApiError(response, "Failed to roll back import");
	}

	const body = (await response.json()) as { data: ImportJob };
	return body.data;
};

/**
 * Start the extraction pipeline for a PDF import job. The server answers 202 once the
 * Step Functions execution has been started.
 */

export const startImportExecution = async (brand: Brand, jobId: string): Promise<void> => {
	const baseUrl = getApiBaseUrl(brand);
	const response = await authFetch(`${baseUrl}/imports/${encodeURIComponent(jobId)}/execution`, {
		method: "POST",
		headers: JSON_HEADERS,
	});

	if (!response.ok) {
		throw await buildApiError(response, "Failed to start import execution");
	}
};
